/**
 * Data Source Cache Utilities
 * Functions for caching data source requests to prevent duplicate API calls
 *
 * This utility solves the issue of duplicate API calls when multiple form fields
 * use the same data source service with different transformations. It works by:
 *
 * 1. Generating a unique cache key for each data source request based on:
 *    - The service function or URL
 *    - Request parameters (with form value substitutions)
 *    - HTTP method (for URL data sources)
 *
 * 2. Storing in-flight requests in a cache map
 *
 * 3. Returning the cached promise for duplicate requests
 *
 * 4. Automatically removing requests from cache once they complete
 */

/**
 * Generate a cache key for a data source request
 * @param dataSource Data source configuration
 * @param formValues Current form values
 * @returns A unique string key for caching
 */
export const generateCacheKey = (
  dataSource: any,
  formValues: Record<string, any> = {}
): string => {
  // For function type data sources
  if (dataSource.type === 'function') {
    // Get service function name
    const serviceFn = dataSource.service
    const fnName = serviceFn.name || 'anonymous'

    // Include parameters in the cache key
    const paramsKey = dataSource.params
      ? JSON.stringify(processParams(dataSource.params, formValues))
      : ''

    // Only use function name without hash
    return `func_${fnName}_${paramsKey}`
  }

  // For URL type data sources
  if (dataSource.type === 'url') {
    // Include URL, method, and parameters in the cache key
    const paramsKey = dataSource.params
      ? JSON.stringify(processParams(dataSource.params, formValues))
      : ''
    const bodyKey = dataSource.body
      ? JSON.stringify(processParams(dataSource.body, formValues))
      : ''

    return `url_${dataSource.url}_${dataSource.method || 'GET'}_${paramsKey}_${bodyKey}`
  }

  // Fallback for unknown types - use just the key without timestamp
  return `unknown_${dataSource.key}`
}

/**
 * Process parameters by replacing form value references
 * @param params Parameters object with potential form value references
 * @param formValues Current form values
 * @returns Processed parameters with actual values
 */
const processParams = (
  params: Record<string, any>,
  formValues: Record<string, any>
): Record<string, any> => {
  const result = { ...params }

  Object.keys(result).forEach(key => {
    const value = result[key]
    if (typeof value === 'string' && value.match(/^\{.*\}$/)) {
      const fieldName = value.slice(1, -1)
      result[key] = formValues[fieldName] || ''
    }
  })

  return result
}

/**
 * DataSourceCache class for managing data source request caching
 *
 * This class provides methods to:
 * - Cache and retrieve data source requests
 * - Clear the entire cache or specific entries
 * - Check if a request is currently cached
 */
export class DataSourceCache {
  private cache: Map<string, Promise<any>>
  private stats: {
    hits: number
    misses: number
    totalRequests: number
  }

  constructor() {
    this.cache = new Map()
    this.stats = {
      hits: 0,
      misses: 0,
      totalRequests: 0
    }
  }

  /**
   * Get a cached request or add a new one to the cache
   * @param cacheKey Unique key for the request
   * @param requestFn Function that returns a promise with the request
   * @returns Promise with the request result
   */
  async getOrAdd(
    cacheKey: string,
    requestFn: () => Promise<any>
  ): Promise<any> {
    this.stats.totalRequests++

    // Check if we have this request in cache
    if (this.cache.has(cacheKey)) {
      this.stats.hits++
      return this.cache.get(cacheKey)
    }

    // Cache miss
    this.stats.misses++

    // Create new request
    let requestPromise = requestFn()

    // Store the original promise in the cache
    this.cache.set(cacheKey, requestPromise)

    try {
      // Wait for the request to complete
      const result = await requestPromise

      // Create a resolved promise with the result and store it in the cache
      // This way, the cache entry remains even after the request completes
      this.cache.set(cacheKey, Promise.resolve(result))

      return result
    } catch (error) {
      // Remove failed requests from cache
      this.cache.delete(cacheKey)
      throw error
    }
  }

  /**
   * Clear all cached requests
   */
  clear(): void {
    this.cache.clear()
  }

  /**
   * Clear a specific cached request
   * @param cacheKey Unique key for the request to clear
   * @returns Whether the entry was successfully removed
   */
  clearKey(cacheKey: string): boolean {
    const result = this.cache.delete(cacheKey)
    return result
  }

  /**
   * Check if a request is currently in the cache
   * @param cacheKey Unique key for the request
   * @returns Whether the request is cached
   */
  has(cacheKey: string): boolean {
    return this.cache.has(cacheKey)
  }

  /**
   * Get the number of currently cached requests
   * @returns The cache size
   */
  get size(): number {
    return this.cache.size
  }

  /**
   * Get cache hit rate as percentage
   * @returns Hit rate percentage
   */
  hitRate(): number {
    if (this.stats.totalRequests === 0) return 0
    return Math.round((this.stats.hits / this.stats.totalRequests) * 100)
  }

  /**
   * Get cache statistics
   * @returns Cache statistics object
   */
  getStats(): {
    hits: number
    misses: number
    totalRequests: number
    hitRate: number
  } {
    return {
      ...this.stats,
      hitRate: this.hitRate()
    }
  }

  /**
   * Reset cache statistics
   */
  resetStats(): void {
    this.stats = {
      hits: 0,
      misses: 0,
      totalRequests: 0
    }
  }

  /**
   * Get all cache keys
   * @returns Array of all cache keys
   */
  getKeys(): string[] {
    return Array.from(this.cache.keys())
  }

  /**
   * Manually add an entry to the cache
   * @param cacheKey Unique key for the cache entry
   * @param data Data to cache
   */
  add(cacheKey: string, data: any): void {
    this.cache.set(cacheKey, Promise.resolve(data))
  }
}

// Export a singleton instance for use across the application
export const dataSourceCache = new DataSourceCache()
